import React, { Dispatch, ReactElement, SetStateAction, useEffect, useState } from "react";
import { StriimInputField, StriimTypography, StriimMessageBox } from "@striim/striim-ui";
import { AppTypeInfo } from "./adapter-selection/app-types-info";
import { components } from "react-select";
import { Grid, useTheme, SvgIcon } from "@mui/material";
import { getSupportedAppType } from "../services";

import { adapterType } from "../create-app-page-templates";
import styles from "../create-app-page-styles";
import APPTYPE from "../services/create-app-page-app-types.const.json";
import licenseService from "../../../../../../core/services/metaStoreService/license-service";
import { createAppUtils } from "../create-app-page-utils";
import { StriimLink } from "@striim/striim-ui-v2";
import { useNavigate } from "react-router-dom";
import WarningIcon from "../../../../../../app/images/striimline/checkpoint-info_trailing.svg";

interface CreateAppTypeDropdownComponentProps {
    sourceAdapter: adapterType;
    targetAdapter: adapterType;
    templateInfo: optionsType;
    isTopologyUnSupported: boolean;
    setTemplateInfo: Dispatch<SetStateAction<optionsType>>;
    handleChange: (value: optionsType, cb: Dispatch<SetStateAction<optionsType>>, type: string) => void;
}

interface CustomSelectOptionProps {
    data: { label: string; value: string; isEnabled?: boolean };
    innerProps: object;
    isSelected: boolean;
    targetAdapter: string;
}

interface appTypeInfoType {
    title: string;
    content: ReactElement;
    icon: ReactElement;
    additionalComponent?: ReactElement;
}

export interface optionsType {
    label: string;
    value: string;
    url?: string;
    isEnabled?: boolean;
}

const CustomSelectOption: React.FunctionComponent<CustomSelectOptionProps> = ({
    data,
    innerProps,
    isSelected,
    targetAdapter,
    ...props
}) => {
    const optionInfo: appTypeInfoType = AppTypeInfo(targetAdapter)[data?.value];
    const theme = useTheme();
    const isDisabled = data?.isEnabled === false;

    return (
        <components.Option
            {...props}
            innerProps={{
                ...innerProps,
                style: {
                    ...innerProps.style,
                    ...styles.dropdownOption(theme),
                    ...(isSelected && styles.selectedOption(theme)),
                    ...(isDisabled && styles.disabledOption)
                }
            }}
        >
            <Grid container sx={styles.optionGrid} data-test-id={`option-${optionInfo.title}`}>
                <Grid item sx={{ position: "relative" }}>
                    {optionInfo?.icon}
                </Grid>
                <Grid item sx={styles.optionContentGird}>
                    <Grid item>
                        <StriimTypography variant="body4" color={isSelected ? "primary.800" : "greyscale.700"}>
                            {optionInfo?.title}
                        </StriimTypography>
                    </Grid>
                    <Grid item>
                        <StriimTypography variant="caption3" color="greyscale.700">
                            {optionInfo?.content}
                        </StriimTypography>
                    </Grid>
                    {optionInfo?.additionalComponent && <Grid item>{optionInfo?.additionalComponent}</Grid>}
                </Grid>
            </Grid>
        </components.Option>
    );
};

const desiredOrder = ["AUTOMATEDILCDC", "IL", "CDC"];

const fetchDropdownHeading = (license, targetAdapter = null) => {
    const dropdownHeading = {
        SQL2FABRICSOLUTION: "Mirroring Options",
        DEFAULT: "What type of App would you like to create?"
    };
    if (targetAdapter === "FabricMirrorWriter") {
        return dropdownHeading[license] || dropdownHeading.DEFAULT;
    }
    return dropdownHeading.DEFAULT;
};

const CreateAppTypeDropdownComponent: React.FunctionComponent<CreateAppTypeDropdownComponentProps> = ({
    sourceAdapter,
    targetAdapter,
    templateInfo,
    isTopologyUnSupported,
    setTemplateInfo,
    handleChange
}) => {
    const [supportedFlows, setSupportedFlows] = useState<optionsType[]>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const supportedAppType = getSupportedAppType(sourceAdapter, targetAdapter);

        const orderedSelectOptions = supportedAppType?.sort(
            (a, b) => desiredOrder.indexOf(a.value) - desiredOrder.indexOf(b.value)
        );
        setSupportedFlows(orderedSelectOptions);

        if (supportedAppType && !templateInfo) {
            const defaultAppType =
                supportedAppType.length === 1
                    ? supportedAppType[0]
                    : supportedAppType.find(flow => flow.value === APPTYPE.AUTOMATEDILCDC);
            if (defaultAppType) {
                appendWizardURL(defaultAppType);
            }
        } else {
            appendWizardURL(templateInfo);
        }
    }, [targetAdapter]);

    const appendWizardURL = (templateInfo = {}) => {
        const url = createAppUtils.generateTemplateURL(
            sourceAdapter.adapter,
            targetAdapter.adapter,
            templateInfo?.value
        );
        handleChange({ ...templateInfo, url } as optionsType, setTemplateInfo, "template");
    };

    return (
        <Grid sx={{ width: "500px" }}>
            {!isTopologyUnSupported && supportedFlows && (
                <StriimInputField
                    label={fetchDropdownHeading(licenseService?.nonSegmentedlicenseName, targetAdapter?.adapter)}
                    placeholder={"Choose"}
                    id="data-test-id-type-of-app"
                    required
                    select
                    value={templateInfo}
                    SelectProps={{
                        options: supportedFlows,
                        components: {
                            Option: props => <CustomSelectOption targetAdapter={targetAdapter?.adapter} {...props} />
                        },
                        isClearable: false,
                        isOptionDisabled: (option: optionsType) => option?.isEnabled === false
                    }}
                    onChange={templateInfo => {
                        appendWizardURL(templateInfo);
                    }}
                    disabled={supportedFlows?.length === 1}
                    sx={{
                        width: "500px",
                        mb: 2
                    }}
                ></StriimInputField>
            )}
            {isTopologyUnSupported && (
                <StriimMessageBox
                    type="WARNING"
                    customIcon={<SvgIcon component={WarningIcon} />}
                    customCardStyles={styles.messageBox}
                    text={
                        <StriimTypography variant="body4" color="greyscale.900">
                            Striim does not offer wizard for {sourceAdapter?.id} &rarr; {targetAdapter?.id}.
                            <StriimLink sx={{ display: "contents" }} onClick={() => navigate("/create-app/name-app")}>
                                {" "}
                                Build a custom App{" "}
                            </StriimLink>{" "}
                            to read from {sourceAdapter?.id} and write to {targetAdapter?.id}.
                        </StriimTypography>
                    }
                ></StriimMessageBox>
            )}
        </Grid>
    );
};

export default CreateAppTypeDropdownComponent;
